import React, { useState } from "react";
import {
  TextField,
  Grid,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { Plus, Trash2, Edit, Check, X } from "lucide-react";
import ProductSection from "../ProductSection";

const BatchTracking = ({
  productData,
  updateProductData,
  errors,
  isSingleSize,
}) => {
  const [showAddBatch, setShowAddBatch] = useState(false);
  const [batchData, setBatchData] = useState({
    batchNumber: "",
    mrp: 0,
    initialQuantity: 0,
    currentQuantity: 0,
    purchasePrice: 0,
    manufacturingDate: "",
    expiryDate: "",
  });
  const [editingIndex, setEditingIndex] = useState(null);

  const handleBatchChange = (field, value, type = "text") => {
    const numValue = type === "number" ? parseFloat(value) || 0 : value;

    setBatchData(prev => ({
      ...prev,
      [field]: numValue,
      // If setting initial quantity, also set current quantity to the same value
      ...(field === "initialQuantity" ? { currentQuantity: numValue } : {}),
    }));
  };

  const handleAddBatch = () => {
    if (editingIndex !== null) {
      // Update existing batch
      const updatedBatches = [...productData.batches];
      updatedBatches[editingIndex] = batchData;
      updateProductData({ batches: updatedBatches });
      setEditingIndex(null);
    } else {
      // Add new batch
      updateProductData({
        batches: [...productData.batches, batchData],
      });
    }

    // Reset form
    setBatchData({
      batchNumber: "",
      mrp: 0,
      initialQuantity: 0,
      currentQuantity: 0,
      purchasePrice: 0,
      manufacturingDate: "",
      expiryDate: "",
    });
    setShowAddBatch(false);
  };

  const handleEditBatch = index => {
    setBatchData(productData.batches[index]);
    setEditingIndex(index);
    setShowAddBatch(true);
  };

  const handleDeleteBatch = index => {
    const updatedBatches = [...productData.batches];
    updatedBatches.splice(index, 1);
    updateProductData({ batches: updatedBatches });
  };

  return (
    <div className="space-y-6">
      <ProductSection data="Batch Tracking" />

      <div className="bg-blue-50 p-3 rounded-lg mb-4">
        <p className="text-blue-700 font-outfit text-xs">
          Batch tracking allows you to manage inventory by specific batches,
          track expiry dates, and maintain batch-specific pricing.
        </p>
      </div>

      <div className="border border-gray-200 rounded-lg p-4">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-sm font-medium font-outfit">Batches</h3>

          <button
            onClick={() => {
              setShowAddBatch(true);
              setEditingIndex(null);
              setBatchData({
                batchNumber: "",
                mrp: 0,
                initialQuantity: 0,
                currentQuantity: 0,
                purchasePrice: 0,
                manufacturingDate: "",
                expiryDate: "",
              });
            }}
            className="px-3 py-1.5 bg-[#5652B0] text-white rounded-lg text-xs font-medium hover:bg-[#4A47A3] flex items-center gap-1">
            <Plus size={14} />
            Add Batch
          </button>
        </div>

        {productData.batches.length === 0 && !showAddBatch ? (
          <div className="bg-gray-50 p-4 rounded-lg text-center">
            <p className="text-gray-500 text-sm mb-2">No batches added yet</p>
            <button
              onClick={() => {
                setShowAddBatch(true);
                setEditingIndex(null);
              }}
              className="px-3 py-1.5 border border-[#5652B0] text-[#5652B0] rounded-lg text-xs font-medium hover:bg-[#F9F8FE]">
              Add Your First Batch
            </button>
          </div>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: "0.5rem" }}>
            <Table size="small">
              <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
                <TableRow>
                  <TableCell
                    sx={{
                      fontFamily: "Outfit",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                    }}>
                    Batch Number
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "Outfit",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                    }}>
                    MRP (₹)
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "Outfit",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                    }}>
                    Initial Qty
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "Outfit",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                    }}>
                    Current Qty
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "Outfit",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                    }}>
                    Purchase Price (₹)
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "Outfit",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                    }}>
                    Mfg. Date
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "Outfit",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                    }}>
                    Exp. Date
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "Outfit",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      width: "100px",
                    }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {productData.batches.map((batch, index) => (
                  <TableRow key={index}>
                    <TableCell
                      sx={{ fontFamily: "Outfit", fontSize: "0.75rem" }}>
                      {batch.batchNumber}
                    </TableCell>
                    <TableCell
                      sx={{ fontFamily: "Outfit", fontSize: "0.75rem" }}>
                      ₹{batch.mrp}
                    </TableCell>
                    <TableCell
                      sx={{ fontFamily: "Outfit", fontSize: "0.75rem" }}>
                      {batch.initialQuantity}
                    </TableCell>
                    <TableCell
                      sx={{ fontFamily: "Outfit", fontSize: "0.75rem" }}>
                      {batch.currentQuantity}
                    </TableCell>
                    <TableCell
                      sx={{ fontFamily: "Outfit", fontSize: "0.75rem" }}>
                      ₹{batch.purchasePrice}
                    </TableCell>
                    <TableCell
                      sx={{ fontFamily: "Outfit", fontSize: "0.75rem" }}>
                      {batch.manufacturingDate}
                    </TableCell>
                    <TableCell
                      sx={{ fontFamily: "Outfit", fontSize: "0.75rem" }}>
                      {batch.expiryDate}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <IconButton
                          size="small"
                          onClick={() => handleEditBatch(index)}
                          sx={{ color: "#5652B0", padding: "4px" }}>
                          <Edit size={14} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteBatch(index)}
                          sx={{ color: "#d32f2f", padding: "4px" }}>
                          <Trash2 size={14} />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}

                {showAddBatch && (
                  <TableRow>
                    <TableCell>
                      <TextField
                        placeholder="Batch #"
                        value={batchData.batchNumber}
                        onChange={e =>
                          handleBatchChange("batchNumber", e.target.value)
                        }
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputProps={{
                          sx: {
                            borderRadius: "0.5rem",
                            fontFamily: "Outfit",
                            fontSize: "0.75rem",
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        placeholder="MRP"
                        type="number"
                        value={batchData.mrp}
                        onChange={e =>
                          handleBatchChange("mrp", e.target.value, "number")
                        }
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">₹</InputAdornment>
                          ),
                          sx: {
                            borderRadius: "0.5rem",
                            fontFamily: "Outfit",
                            fontSize: "0.75rem",
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        placeholder="Initial"
                        type="number"
                        value={batchData.initialQuantity}
                        onChange={e =>
                          handleBatchChange(
                            "initialQuantity",
                            e.target.value,
                            "number",
                          )
                        }
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputProps={{
                          sx: {
                            borderRadius: "0.5rem",
                            fontFamily: "Outfit",
                            fontSize: "0.75rem",
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        placeholder="Current"
                        type="number"
                        value={batchData.currentQuantity}
                        onChange={e =>
                          handleBatchChange(
                            "currentQuantity",
                            e.target.value,
                            "number",
                          )
                        }
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputProps={{
                          sx: {
                            borderRadius: "0.5rem",
                            fontFamily: "Outfit",
                            fontSize: "0.75rem",
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        placeholder="Purchase"
                        type="number"
                        value={batchData.purchasePrice}
                        onChange={e =>
                          handleBatchChange(
                            "purchasePrice",
                            e.target.value,
                            "number",
                          )
                        }
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">₹</InputAdornment>
                          ),
                          sx: {
                            borderRadius: "0.5rem",
                            fontFamily: "Outfit",
                            fontSize: "0.75rem",
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        placeholder="Mfg Date"
                        type="date"
                        value={batchData.manufacturingDate}
                        onChange={e =>
                          handleBatchChange("manufacturingDate", e.target.value)
                        }
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputProps={{
                          sx: {
                            borderRadius: "0.5rem",
                            fontFamily: "Outfit",
                            fontSize: "0.75rem",
                          },
                        }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        placeholder="Exp Date"
                        type="date"
                        value={batchData.expiryDate}
                        onChange={e =>
                          handleBatchChange("expiryDate", e.target.value)
                        }
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputProps={{
                          sx: {
                            borderRadius: "0.5rem",
                            fontFamily: "Outfit",
                            fontSize: "0.75rem",
                          },
                        }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <IconButton
                          size="small"
                          onClick={handleAddBatch}
                          disabled={
                            !batchData.batchNumber ||
                            batchData.initialQuantity <= 0
                          }
                          sx={{
                            color: "#5652B0",
                            padding: "4px",
                            "&.Mui-disabled": {
                              color: "rgba(0, 0, 0, 0.26)",
                            },
                          }}>
                          <Check size={14} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setShowAddBatch(false);
                            setEditingIndex(null);
                          }}
                          sx={{ color: "#d32f2f", padding: "4px" }}>
                          <X size={14} />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </div>
    </div>
  );
};

export default BatchTracking;
